import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Input, Spinner } from '@easydent/shared-ui';
import { getCreatives, createCreative, requestCreativeUpload, uploadFileToUrl, type Creative } from '@/lib/api';

const statusClass: Record<string, string> = {
  Approved: 'bg-brand-50 text-brand-ink-strong',
  Pending: 'bg-amber-50 text-amber-700',
  Rejected: 'bg-red-50 text-red-600',
};

const CreativesPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Creative[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', mediaType: 'Image', imageUrl: '', targetUrl: '' });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => getCreatives().then(setRows).catch(() => setError(t('creatives.loadError', "Could not load creatives.")));
  useEffect(() => { load(); }, []);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const { objectPath, uploadUrl } = await requestCreativeUpload(file.name, file.type);
      await uploadFileToUrl(uploadUrl, file);
      setForm((f) => ({ ...f, imageUrl: objectPath }));
      setUploadedName(file.name);
    } catch {
      setError(t('creatives.uploadError', "Could not upload the image. You can paste an https URL instead."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await createCreative(form);
      setForm({ title: '', mediaType: 'Image', imageUrl: '', targetUrl: '' });
      setUploadedName(null);
      setCreating(false);
      await load();
    } catch (err) {
      setError((err as { response?: { data?: { title?: string; errors?: Record<string, string[]> } } })?.response?.data?.title
        ?? Object.values((err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors ?? {}).flat()[0]
        ?? t('creatives.createError', "Could not create creative."));
    } finally { setBusy(false); }
  };

  if (error && !rows) return <Alert variant="error">{error}</Alert>;
  if (!rows) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-body">{t('creatives.title', "Creatives")}</h1>
        <Button onClick={() => setCreating((v) => !v)}>{creating ? t('common.cancel', "Cancel") : t('creatives.new', "New creative")}</Button>
      </div>
      {error && <Alert variant="error">{error}</Alert>}

      {creating && (
        <Card>
          <form onSubmit={submit} className="grid gap-3">
            <Input placeholder={t('creatives.titlePlaceholder', "Title")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />

            {/* Upload an image directly, or paste an external https URL. */}
            <div className="grid gap-1.5">
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPickFile} className="hidden" />
                <Button type="button" variant="secondary" loading={uploading} onClick={() => fileRef.current?.click()}>
                  {uploadedName ? t('creatives.uploadImage', "Change image") : t('creatives.uploadImage', "Upload image")}
                </Button>
                {uploadedName && <span className="truncate text-xs text-brand-ink-strong">✓ {uploadedName}</span>}
              </div>
              <div className="text-center text-xs text-subtle-ink">{t('creatives.or', "— or —")}</div>
              <Input
                placeholder={t('creatives.imageUrlPlaceholder', "Image URL (https://\u2026)")}
                value={form.imageUrl.startsWith('creatives/') ? '' : form.imageUrl}
                onChange={(e) => { setForm({ ...form, imageUrl: e.target.value }); setUploadedName(null); }}
              />
            </div>

            <Input placeholder={t('creatives.targetUrlPlaceholder', "Click-through URL (https://\u2026)")} value={form.targetUrl} onChange={(e) => setForm({ ...form, targetUrl: e.target.value })} required />
            <p className="text-xs text-muted">{t('creatives.reviewNote', "New creatives are reviewed by easyDent before they can run.")}</p>
            <div><Button loading={busy} disabled={!form.title || !form.imageUrl || !form.targetUrl}>{t('creatives.submit', "Submit for review")}</Button></div>
          </form>
        </Card>
      )}

      {rows.length === 0 ? (
        <Card><p className="py-6 text-center text-sm text-subtle-ink">{t('creatives.empty', "No creatives yet.")}</p></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {rows.map((c) => (
            <Card key={c.id}>
              <img src={c.imageUrl} alt={c.title} className="mb-3 max-h-40 w-full rounded-lg object-contain" />
              <div className="flex items-center justify-between">
                <span className="font-semibold text-body">{c.title}</span>
                <span className={`rounded px-2 py-0.5 text-xs ${statusClass[c.status] ?? 'bg-subtle text-muted'}`}>{c.status}</span>
              </div>
              {c.reviewNote && <div className="mt-1 text-xs text-red-500">Note: {c.reviewNote}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreativesPage;
